import { useCallback, useContext, useEffect, useState, useRef, useMemo } from 'react';
import SpeechRecognition, { useSpeechRecognition as useSpeechRecLib } from 'react-speech-recognition';
import { AppPlatform } from '@trinity/chat';
import { RecordingEnd } from '@trinity/chat-components/components/Sounds/RecordingEnd';
import { RecordingStart } from '@trinity/chat-components/components/Sounds/RecordingStart';
import { AppPlatformContext, GroupFeaturesContext, SessionManagerContext, WebsocketConfigContext } from '@trinity/chat-components/containers/Chat';
import { useTelemetryLogger } from '@trinity/observability-web/react';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useMicrophoneStateContextNullSafe } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { createAzureSpeechRecognitionPonyfill } from '@trinity/chat-components/lib/azure.speech';
import { createAwsSpeechServicesPonyfill } from '@trinity/chat-components/speech-recognition-aws-polyfill/recognizers/aws';
import { createCustomBackendSpeechRecognitionPonyfill } from '@trinity/chat-components/speech-recognition-custom-polyfill/recognizers/customBackend';
import { ActiveChatSessionContext } from '../context';

// Debug flag - set to true to enable console.log statements
const DEBUG_SPEECH_RECOGNITION = false;

// Debug logging wrapper functions
const debugLog = (..._args: any[]) => {
  if (DEBUG_SPEECH_RECOGNITION) console.warn(..._args);
};

const debugWarn = (...args: any[]) => {
  if (DEBUG_SPEECH_RECOGNITION) console.warn(...args);
};

type Props = {
  continuous?: boolean;
  onResult: (result: string, prefix: string) => void;
  onInterimResult?: (result: string, prefix: string) => void;
  onAudioLevelChange: (level: number) => void;
  timeout?: number;
  voicemode?: boolean;
  onVoiceModeAutoSubmit?: () => void;
};

export type MicState = 'inactive' | 'waiting-for-permission' | 'listening' | 'error';

const SPEECH_TIMEOUT_MS = 7000;
const SILENCE_THRESHOLD = 5; // Audio level below this is considered silence
const SILENCE_TIMEOUT_MS = 5000; // Wait 5 seconds after speech ends before stopping (normal mode)
const VOICE_MODE_SILENCE_TIMEOUT_MS = 1500; // Wait 1.5 seconds in voice mode for faster response
const MAX_LISTENING_TIME_MS = 180000; // Maximum 3 minutes of listening

const useSpeechServicesProvider = () => {
  const features = useContext(GroupFeaturesContext);
  const appPlatform = useContext(AppPlatformContext);
  return useMemo(() => {
    const speechServicesProvider = features.speechServicesProvider;
    if (appPlatform === AppPlatform.enum.veeva) return 'custom';
    return speechServicesProvider;
  }, [appPlatform, features.speechServicesProvider]);
};

export const useSpeechRecognition = ({
  continuous,
  onResult: onResultProp,
  onAudioLevelChange,
  onInterimResult,
  timeout: _timeout = SPEECH_TIMEOUT_MS,
  voicemode,
  onVoiceModeAutoSubmit: _onVoiceModeAutoSubmit,
}: Props) => {
  const timeoutRef = useRef<NodeJS.Timeout>(undefined);
  const silenceTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const maxTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const lastAudioLevelRef = useRef<number>(0);
  const silenceStartTimeRef = useRef<number>(0);
  const hasDetectedSpeechRef = useRef<boolean>(false);
  const speechSampleCountRef = useRef<number>(0);
  const resultPrefix = useRef<string>(undefined);
  const lastInterimTranscriptRef = useRef<string>('');

  const audioContextRef = useRef<AudioContext>(null);
  const mediaStreamRef = useRef<MediaStream>(null);
  const timerRef = useRef<NodeJS.Timeout>(null);

  const intentionalStopRef = useRef<boolean>(false);

  const { listening: libListening, finalTranscript, resetTranscript, transcript } = useSpeechRecLib({ clearTranscriptOnListen: true });
  const [errorMsg, setErrorMsg] = useState('');
  const [micState, setMicState] = useMicrophoneStateContextNullSafe();
  const speechProvider = useSpeechServicesProvider();
  const chat = useContext(ActiveChatSessionContext);

  const lastPolyfillRef = useRef<{ speechProvider: string; chatIdentifier: string; sessionId?: string } | null>(null);

  const apiClient = useChatApiClient();
  const socketOptions = useContext(WebsocketConfigContext);
  const sessionManager = useContext(SessionManagerContext);
  const sessionId = sessionManager?.sessionId;

  const loggerCtx = useTelemetryLogger();

  const cleanupAudioResources = useCallback(() => {
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(e => {
        loggerCtx.captureException(e);
      });
      audioContextRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (mediaStreamRef.current) {
      for (const track of mediaStreamRef.current.getTracks()) {
        track.stop();
      }
      mediaStreamRef.current = null;
    }
  }, [loggerCtx]);

  const stopListeningWithCleanup = useCallback(async () => {
    intentionalStopRef.current = true;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = undefined;
    }
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = undefined;
    }

    cleanupAudioResources();

    try {
      if (speechProvider === 'azure-speech-services') {
        await SpeechRecognition.abortListening();
      } else {
        await SpeechRecognition.stopListening();
      }
    } catch (e) {
      loggerCtx.captureException(e);
    }

    resetTranscript();
  }, [loggerCtx, speechProvider, resetTranscript, cleanupAudioResources]);

  const handleSpeechDetected = useCallback(() => {
    speechSampleCountRef.current += 1;
    if (!hasDetectedSpeechRef.current && speechSampleCountRef.current >= 2) {
      hasDetectedSpeechRef.current = true;
    }
    if (silenceStartTimeRef.current !== 0) {
      silenceStartTimeRef.current = 0;
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = undefined;
      }
    }
  }, []);

  const handleAudioLevelChangeWithSilence = useCallback((level: number) => {
    lastAudioLevelRef.current = level;
    onAudioLevelChange(level);

    const shouldDetectSilence = libListening && (voicemode || continuous);
    if (!shouldDetectSilence) return;

    const silenceTimeout = voicemode ? VOICE_MODE_SILENCE_TIMEOUT_MS : SILENCE_TIMEOUT_MS;

    if (level > SILENCE_THRESHOLD) {
      handleSpeechDetected();
      return;
    }

    if (!hasDetectedSpeechRef.current) return;

    if (silenceStartTimeRef.current === 0) {
      silenceStartTimeRef.current = Date.now();
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = setTimeout(() => {
        stopListeningWithCleanup().catch(e => {
          loggerCtx.captureException(e);
        });
      }, silenceTimeout);
    }
  }, [onAudioLevelChange, continuous, libListening, voicemode, stopListeningWithCleanup, handleSpeechDetected, loggerCtx]);

  const applyAzurePolyfill = useCallback(() => {
    try {
      debugLog('[useSpeechRecognition] Initializing Azure Speech ponyfill...');
      const ponyfill = createAzureSpeechRecognitionPonyfill(apiClient.speech.fetchAzureSpeechAuthorizationToken);
      debugLog('[useSpeechRecognition] Ponyfill created:', ponyfill);
      const { SpeechRecognition: AzureSpeechRecognition } = ponyfill as unknown as { SpeechRecognition: typeof SpeechRecognition };
      debugLog('[useSpeechRecognition] AzureSpeechRecognition:', AzureSpeechRecognition);
      if (!AzureSpeechRecognition) {
        throw new Error('Azure SpeechRecognition class is undefined');
      }
      SpeechRecognition.applyPolyfill(AzureSpeechRecognition);
      debugLog('[useSpeechRecognition] Ponyfill applied successfully');
      const api = SpeechRecognition.getRecognition();
      debugLog('[useSpeechRecognition] Recognition API after polyfill:', !!api);
      if (!api) {
        throw new Error('SpeechRecognition.getRecognition() returned null after applying polyfill');
      }
    } catch (e) {
      loggerCtx.captureException(e, {
        tags: { speechProvider: 'azure-speech-services' },
        extra: { action: 'fetching_authorization_token' },
      });
      console.error('Error applying Azure Speech ponyfill:', e);
      console.error('Error stack:', e instanceof Error ? e.stack : 'No stack trace');
      setErrorMsg(e instanceof Error ? e.message : String(e));
    }
  }, [apiClient.speech.fetchAzureSpeechAuthorizationToken, loggerCtx]);

  const applyAwsPolyfill = useCallback(() => {
    try {
      const recognition = createAwsSpeechServicesPonyfill({
        signedUrlProvider: async () => {
          const res = await apiClient.speech.fetchAwsSpeechSignedUrl();
          return res.signedUrl;
        },
        continuous,
      });
      SpeechRecognition.applyPolyfill(recognition);
    } catch (e) {
      loggerCtx.captureException(e, {
        tags: { speechProvider: 'aws-speech-services' },
        extra: { action: 'applying_polyfill' },
      });
      console.error('Error applying AWS Speech polyfill:', e);
    }
  }, [apiClient.speech, continuous, loggerCtx]);

  const applyCustomPolyfill = useCallback(() => {
    try {
      if (!socketOptions) {
        throw new Error('WebsocketConfigContext is required for custom speech recognition.');
      }

      const CustomBackendRecognizer = createCustomBackendSpeechRecognitionPonyfill({
        chatIdentifier: chat.identifier,
        continuous,
        websocketOptions: {
          ...socketOptions,
          sessionId,
        },
      });
      SpeechRecognition.applyPolyfill(CustomBackendRecognizer);
    } catch (e) {
      loggerCtx.captureException(e, {
        tags: { speechProvider: 'custom' },
        extra: { action: 'applying_polyfill', chatIdentifier: chat.identifier },
      });
      console.error('Error applying custom backend speech polyfill:', e);
      setErrorMsg(e instanceof Error ? e.message : String(e));
    }
  }, [chat.identifier, continuous, loggerCtx, sessionId, socketOptions]);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const lastPolyfill = lastPolyfillRef.current;
    const needsPolyfillUpdate = !lastPolyfill ||
      lastPolyfill.speechProvider !== speechProvider ||
      lastPolyfill.chatIdentifier !== chat.identifier ||
      lastPolyfill.sessionId !== sessionId;

    if (needsPolyfillUpdate) {
      if (speechProvider === 'azure-speech-services') {
        applyAzurePolyfill();
      } else if (speechProvider === 'aws-speech-services') {
        applyAwsPolyfill();
      } else if (speechProvider === 'custom') {
        applyCustomPolyfill();
      } else {
        debugWarn('[useSpeechRecognition] No polyfill needed for provider:', speechProvider);
      }
      lastPolyfillRef.current = { speechProvider, chatIdentifier: chat.identifier, sessionId };
    }

    const api = SpeechRecognition.getRecognition();
    debugLog('[useSpeechRecognition] Got recognition API:', !!api, 'Provider:', speechProvider);
    function onStart() {
      debugLog('[useSpeechRecognition] onStart event fired');
      loggerCtx.addBreadcrumb({ message: 'Speech recognition started' });
      setMicState('listening');

      silenceStartTimeRef.current = 0;
      lastAudioLevelRef.current = 0;
      hasDetectedSpeechRef.current = false;
      speechSampleCountRef.current = 0;

      if (continuous) {
        clearAllTimers();
        maxTimeoutRef.current = setTimeout(() => {
          loggerCtx.addBreadcrumb({ message: 'Speech recognition stopped due to maximum time limit' });
          stopListeningWithCleanup().catch(err => {
            loggerCtx.captureException(err);
          });
        }, MAX_LISTENING_TIME_MS);
      }

      RecordingStart.play().catch(e => {
        loggerCtx.captureException(e, {
          tags: { audioType: 'recording_start' },
          extra: { action: 'audio_playback' },
        });
        console.error('Failed to play RecordingStart:', e);
      });
    }

    function clearAllTimers() {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
      if (maxTimeoutRef.current) clearTimeout(maxTimeoutRef.current);
    }

    function onEnd() {
      loggerCtx.addBreadcrumb({ message: 'Speech recognition ended' });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = undefined;
      }
      if (maxTimeoutRef.current) {
        clearTimeout(maxTimeoutRef.current);
        maxTimeoutRef.current = undefined;
      }

      silenceStartTimeRef.current = 0;
      lastAudioLevelRef.current = 0;

      setMicState(old => {
        if (old === 'error') {
          return old;
        } else {
          RecordingEnd.play().catch(e => {
            loggerCtx.captureException(e, {
              tags: { audioType: 'recording_end' },
              extra: { action: 'audio_playback' },
            });
            console.error('Failed to play RecordingEnd:', e);
          });
          return 'inactive';
        }
      });
    }

    function onError(e: SpeechRecognitionErrorEvent) {
      if (speechProvider === 'azure-speech-services' && e.error === 'aborted') {
        onEnd();
        return;
      }

      loggerCtx.captureException(e);
      loggerCtx.setContext('SpeechRecognitionError', { error: e.error, message: e.message });
      setMicState('error');
      if (e.error === 'not-allowed') {
        setErrorMsg('Permission denied');
      } else {
        setErrorMsg(e.error);
      }
    }

    if (api) {
      debugLog('[useSpeechRecognition] Setting up event listeners for speech API');
      api.addEventListener('audiostart', onStart);
      api.addEventListener('end', onEnd);
      api.addEventListener('error', onError);

      cleanup = () => {
        api.removeEventListener('audiostart', onStart);
        api.removeEventListener('end', onEnd);
        api.removeEventListener('error', onError);
      };
    } else {
      debugWarn('[useSpeechRecognition] No speech recognition API available');
    }

    return cleanup;
  }, [applyAwsPolyfill, applyAzurePolyfill, applyCustomPolyfill, loggerCtx, apiClient, continuous, speechProvider, chat.identifier, setMicState, voicemode, stopListeningWithCleanup, socketOptions, sessionId]);

  useEffect(() => {
    if (libListening && micState !== 'listening' && micState !== 'error') {
      setMicState('listening');
    }
  }, [libListening, micState, setMicState]);

  useEffect(() => {
    if (!libListening && micState === 'listening') {
      RecordingEnd.play().catch(e => {
        loggerCtx.captureException(e, {
          tags: { audioType: 'recording_end_state_sync' },
          extra: { action: 'audio_playback' },
        });
        console.error('Failed to play RecordingEnd from state sync:', e);
      });
      setMicState('inactive');
    }
  }, [loggerCtx, libListening, micState, setMicState]);

  useEffect(() => {
    if (speechProvider !== 'browser') {
      return;
    }
    const isSupported = SpeechRecognition.browserSupportsSpeechRecognition();
    if (!isSupported) {
      loggerCtx.captureMessage('Speech recognition not supported');
      setMicState('error');
      setErrorMsg('Speech recognition not supported');
    }
  }, [loggerCtx, setMicState, speechProvider]);

  useEffect(() => {
    if (!libListening) {
      debugLog('[useSpeechRecognition] Not listening. finalTranscript:', finalTranscript, 'transcript:', transcript, 'lastInterim:', lastInterimTranscriptRef.current);

      if (finalTranscript) {
        debugLog('[useSpeechRecognition] Calling onResultProp with finalTranscript:', finalTranscript);
        onResultProp(finalTranscript, resultPrefix.current);
        lastInterimTranscriptRef.current = '';
        resetTranscript();
      } else if (transcript) {
        debugLog('[useSpeechRecognition] Calling onResultProp with transcript:', transcript);
        onResultProp(transcript, resultPrefix.current);
        lastInterimTranscriptRef.current = '';
        resetTranscript();
      } else if (lastInterimTranscriptRef.current) {
        debugLog('[useSpeechRecognition] Calling onResultProp with lastInterim:', lastInterimTranscriptRef.current);
        onResultProp(lastInterimTranscriptRef.current, resultPrefix.current);
        lastInterimTranscriptRef.current = '';
      } else {
        debugLog('[useSpeechRecognition] No transcript to deliver');
      }
    }
  }, [libListening, finalTranscript, transcript, resetTranscript, onResultProp]);

  useEffect(() => {
    if (libListening && transcript && onInterimResult) {
      lastInterimTranscriptRef.current = transcript;
      onInterimResult(transcript, resultPrefix.current);
    }
  }, [transcript, libListening, onInterimResult]);

  const stop = useCallback(async () => {
    await stopListeningWithCleanup();
    setMicState('inactive');
  }, [stopListeningWithCleanup, setMicState]);

  useEffect(() => {
    if (libListening) {
      document.body.addEventListener('click', clickHandler);

      return () => document.body.removeEventListener('click', clickHandler);
    }

    function clickHandler(e: MouseEvent) {
      if ((e.target as HTMLElement).tagName.toLowerCase() === 'button') {
        stop().catch(err => {
          loggerCtx.captureException(err);
        });
      }
    }
  }, [libListening, stop, loggerCtx]);

  useEffect(() => {
    const api = SpeechRecognition.getRecognition();

    if (api) {
      api.addEventListener('audiostart', onAudioStart);
      api.addEventListener('audioend', onAudioEnd);

      return () => {
        api.removeEventListener('audiostart', onAudioStart);
        api.removeEventListener('audioend', onAudioEnd);
        cleanupAudioResources();
      };
    }

    function onAudioStart() {
      window.navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
        mediaStreamRef.current = stream;
        audioContextRef.current = new AudioContext();
        const mediaStreamAudioSourceNode = audioContextRef.current.createMediaStreamSource(stream);
        const analyserNode = audioContextRef.current.createAnalyser();
        analyserNode.smoothingTimeConstant = 0.5;
        analyserNode.fftSize = 512;
        analyserNode.minDecibels = -80;

        mediaStreamAudioSourceNode.connect(analyserNode);

        const sampleArray = new Uint8Array(analyserNode.frequencyBinCount);

        const logTime = () => {
          if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
            return;
          }

          analyserNode.getByteFrequencyData(sampleArray);
          let values = 0;

          const length = sampleArray.length;
          for (let i = 0; i < length; i++) {
            values += sampleArray[i];
          }

          const volume = Math.min(21, Math.max(0, 14 * Math.log10(values / length)));

          handleAudioLevelChangeWithSilence(volume);
        };

        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        timerRef.current = setInterval(logTime, 100);
      }).catch(e => {
        loggerCtx.captureException(e);
      });
    }

    function onAudioEnd() {
      cleanupAudioResources();
    }
  }, [loggerCtx, handleAudioLevelChangeWithSilence, cleanupAudioResources]);

  const start = useCallback((prefix?: string) => {
    debugLog('[useSpeechRecognition] start() called, continuous:', continuous, 'micState:', micState);
    intentionalStopRef.current = false;
    resultPrefix.current = prefix;
    if (micState !== 'error') {
      resetTranscript();
      setMicState('waiting-for-permission');

      // Debug: Check the recognition API before calling startListening
      const api = SpeechRecognition.getRecognition();
      debugLog('[useSpeechRecognition] Recognition API before start:', api);
      debugLog('[useSpeechRecognition] API has start method?', api && typeof api.start === 'function');
      debugLog('[useSpeechRecognition] API continuous:', api?.continuous);
      debugLog('[useSpeechRecognition] API lang:', api?.lang);

      try {
        const result = SpeechRecognition.startListening({ continuous, language: 'en-US' });
        debugLog('[useSpeechRecognition] SpeechRecognition.startListening() called, result:', result);

        // Check if it's a promise
        if (result && typeof result.then === 'function') {
          result.then(() => {
            debugLog('[useSpeechRecognition] startListening promise resolved');
          }).catch((err) => {
            console.error('[useSpeechRecognition] startListening promise rejected:', err);
            setMicState('error');
            setErrorMsg(err instanceof Error ? err.message : String(err));
          });
        }
      } catch (err) {
        console.error('[useSpeechRecognition] startListening threw error:', err);
        setMicState('error');
        setErrorMsg(err instanceof Error ? err.message : String(err));
      }
    } else {
      debugWarn('[useSpeechRecognition] Cannot start - micState is error');
    }
  }, [continuous, micState, resetTranscript, setMicState]);

  const listening = useMemo(() => {
    return micState === 'listening';
  }, [micState]);

  const isSupported = useCallback(() => {
    if (speechProvider !== 'browser') {
      return true;
    }
    return SpeechRecognition.browserSupportsSpeechRecognition();
  }, [speechProvider]);

  return {
    listening,
    errorMsg,
    micState,
    interimResult: transcript,
    start,
    stop,
    isSupported,
  };
};
